/**
 * Simplify route coordinates to reduce data size
 * Uses Douglas-Peucker algorithm to reduce points while maintaining shape
 */

interface Point {
  lat: number;
  lng: number;
}

// Calculate perpendicular distance from point to line
function perpendicularDistance(point: Point, lineStart: Point, lineEnd: Point): number {
  const dx = lineEnd.lng - lineStart.lng;
  const dy = lineEnd.lat - lineStart.lat;
  
  if (dx === 0 && dy === 0) {
    // Line start and end are the same
    return Math.sqrt(
      Math.pow(point.lng - lineStart.lng, 2) + 
      Math.pow(point.lat - lineStart.lat, 2)
    );
  }
  
  const t = ((point.lng - lineStart.lng) * dx + (point.lat - lineStart.lat) * dy) / 
            (dx * dx + dy * dy);
  
  const closestPoint = {
    lng: lineStart.lng + t * dx,
    lat: lineStart.lat + t * dy
  };
  
  return Math.sqrt(
    Math.pow(point.lng - closestPoint.lng, 2) + 
    Math.pow(point.lat - closestPoint.lat, 2)
  );
}

// Douglas-Peucker algorithm
function douglasPeucker(points: Point[], epsilon: number): Point[] {
  if (points.length <= 2) return points;
  
  // Find the point with the maximum distance from the line
  let maxDistance = 0;
  let maxIndex = 0;
  
  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(
      points[i], 
      points[0], 
      points[points.length - 1]
    );
    
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  
  // If max distance is greater than epsilon, recursively simplify
  if (maxDistance > epsilon) {
    const leftPoints = douglasPeucker(points.slice(0, maxIndex + 1), epsilon);
    const rightPoints = douglasPeucker(points.slice(maxIndex), epsilon);
    
    // Combine results (remove duplicate point at index)
    return [...leftPoints.slice(0, -1), ...rightPoints];
  } else {
    // All points between can be removed
    return [points[0], points[points.length - 1]];
  }
}

/**
 * Simplify route coordinates to stay under Firestore's 1MB limit
 * @param coordinates Array of [lng, lat] coordinates
 * @param targetSize Target size in bytes (default 500KB to leave room for other data)
 * @returns Simplified coordinates
 */
export function simplifyRouteCoordinates(
  coordinates: [number, number][],
  targetSize: number = 500 * 1024 // 500KB target (more aggressive)
): [number, number][] {
  if (!coordinates || coordinates.length === 0) return coordinates;
  
  // More accurate size estimation including JSON overhead
  const testData = { coordinates: coordinates.slice(0, 100) };
  const sampleSize = JSON.stringify(testData).length;
  const bytesPerCoordinate = sampleSize / 100;
  const estimatedSize = coordinates.length * bytesPerCoordinate;
  
  console.log(`📏 Route size estimation: ${(estimatedSize / 1024).toFixed(1)}KB for ${coordinates.length} points`);
  
  // If already small enough, return as-is
  if (estimatedSize <= targetSize) return coordinates;
  
  // Calculate target number of points
  const targetPoints = Math.floor(targetSize / bytesPerCoordinate * 0.8); // 80% to be safe
  console.log(`🎯 Target: reduce to ~${targetPoints} points from ${coordinates.length}`);
  
  // Convert to Point format
  const points: Point[] = coordinates.map(([lng, lat]) => ({ lng, lat }));
  
  // Binary search for the right epsilon value
  let minEpsilon = 0.000001; // ~0.1 meters
  let maxEpsilon = 0.1; // ~10km
  let bestSimplified = points;
  let attempts = 0;
  
  while (attempts < 20 && minEpsilon < maxEpsilon) {
    const epsilon = (minEpsilon + maxEpsilon) / 2;
    const simplified = douglasPeucker(points, epsilon);
    
    if (simplified.length <= targetPoints) {
      bestSimplified = simplified;
      maxEpsilon = epsilon;
    } else {
      minEpsilon = epsilon;
    }
    
    // If we're close enough, stop
    if (Math.abs(simplified.length - targetPoints) < targetPoints * 0.1) {
      bestSimplified = simplified;
      break;
    }
    
    attempts++;
  }
  
  // If still too large, use uniform sampling as fallback
  if (bestSimplified.length > targetPoints) {
    console.warn(`⚠️ Douglas-Peucker not aggressive enough, using uniform sampling`);
    const step = Math.ceil(points.length / targetPoints);
    const sampled: Point[] = [];
    
    // Always include first point
    sampled.push(points[0]);
    
    // Sample points uniformly
    for (let i = step; i < points.length - 1; i += step) {
      sampled.push(points[i]);
    }
    
    // Always include last point
    sampled.push(points[points.length - 1]);
    
    bestSimplified = sampled;
  }
  
  console.log(`✂️ Simplified from ${coordinates.length} to ${bestSimplified.length} points`);
  
  // Convert back to coordinate format
  return bestSimplified.map(point => [point.lng, point.lat]);
}

/**
 * Calculate the approximate size of a route segment in bytes
 */
export function calculateRouteSegmentSize(route: any): number {
  // Rough estimation of JSON size
  const jsonString = JSON.stringify(route);
  return new Blob([jsonString]).size;
}

/**
 * Simplify coordinates based on map zoom level for efficient rendering
 * @param coordinates Array of [lng, lat] coordinates
 * @param zoom Current map zoom level
 * @param surfaces Optional array of surface types for each coordinate
 * @returns Simplified coordinates optimized for the zoom level
 */
export function simplifyCoordinatesForZoom(
  coordinates: [number, number][],
  zoom: number,
  surfaces?: string[]
): [number, number][] {
  if (!coordinates || coordinates.length === 0) return coordinates;
  
  // If we have surface data, use surface-aware simplification
  if (surfaces && surfaces.length === coordinates.length) {
    return simplifyCoordinatesWithSurfaces(coordinates, surfaces, zoom);
  }
  
  // Determine epsilon based on zoom level
  // Higher zoom = more detail needed = smaller epsilon
  let epsilon: number;
  
  if (zoom < 8) {
    epsilon = 0.001; // Very simplified for overview
  } else if (zoom < 12) {
    epsilon = 0.0001; // Moderate simplification
  } else if (zoom < 15) {
    epsilon = 0.00001; // Light simplification
  } else {
    epsilon = 0.000001; // Minimal simplification for detailed view
  }
  
  // Convert to Point format
  const points: Point[] = coordinates.map(([lng, lat]) => ({ lng, lat }));
  
  // Apply Douglas-Peucker algorithm
  const simplified = douglasPeucker(points, epsilon);
  
  // Convert back to coordinate format
  return simplified.map(point => [point.lng, point.lat]);
}

/**
 * Surface-aware coordinate simplification that preserves surface transition points
 */
function simplifyCoordinatesWithSurfaces(
  coordinates: [number, number][],
  surfaces: string[],
  zoom: number
): [number, number][] {
  if (coordinates.length <= 2) return coordinates;
  
  // Find surface transition points that must be preserved
  const transitionIndices = new Set<number>();
  
  // Always preserve first and last points
  transitionIndices.add(0);
  transitionIndices.add(coordinates.length - 1);
  
  // Find surface transitions
  for (let i = 1; i < surfaces.length; i++) {
    if (surfaces[i] !== surfaces[i - 1]) {
      // Surface change detected - preserve both the last point of old surface
      // and first point of new surface
      transitionIndices.add(i - 1);
      transitionIndices.add(i);
    }
  }
  
  // Determine epsilon based on zoom level (more conservative to preserve accuracy)
  let epsilon: number;
  if (zoom < 8) {
    epsilon = 0.0005; // Simplified but preserve transitions
  } else if (zoom < 12) {
    epsilon = 0.00005; // Moderate simplification
  } else if (zoom < 15) {
    epsilon = 0.000005; // Light simplification
  } else {
    epsilon = 0.0000005; // Minimal simplification for detailed view
  }
  
  // Group coordinates by surface type and simplify each segment separately
  const result: [number, number][] = [];
  let segmentStart = 0;
  
  // Sort transition indices to process in order
  const sortedTransitions = Array.from(transitionIndices).sort((a, b) => a - b);
  
  for (let i = 0; i < sortedTransitions.length - 1; i++) {
    const start = sortedTransitions[i];
    const end = sortedTransitions[i + 1];
    
    if (end - start <= 1) {
      // Single point or adjacent points - just add them
      if (result.length === 0 || result[result.length - 1] !== coordinates[start]) {
        result.push(coordinates[start]);
      }
      if (start !== end) {
        result.push(coordinates[end]);
      }
      continue;
    }
    
    // Extract segment for this surface type
    const segmentCoords = coordinates.slice(start, end + 1);
    const points: Point[] = segmentCoords.map(([lng, lat]) => ({ lng, lat }));
    
    // Apply Douglas-Peucker to this surface-homogeneous segment
    const simplified = douglasPeucker(points, epsilon);
    
    // Add to result (avoid duplicating transition points)
    const startIdx = result.length === 0 ? 0 : 1; // Skip first point if not the very first
    for (let j = startIdx; j < simplified.length; j++) {
      result.push([simplified[j].lng, simplified[j].lat]);
    }
  }
  
  // Add the final point if not already added
  const lastPoint = coordinates[coordinates.length - 1];
  if (result.length === 0 || 
      result[result.length - 1][0] !== lastPoint[0] || 
      result[result.length - 1][1] !== lastPoint[1]) {
    result.push(lastPoint);
  }
  
  console.log(`🏗️ Surface-aware simplification: ${coordinates.length} -> ${result.length} points (${transitionIndices.size} transitions preserved)`);
  
  return result;
}

/**
 * Check if route segments need simplification
 */
export function routeNeedsSimplification(route: any): boolean {
  const size = calculateRouteSegmentSize(route);
  console.log(`📊 Route segment size: ${(size / 1024).toFixed(1)}KB`);
  return size > 500 * 1024; // 500KB threshold (more conservative)
}